import type { AutomationProjectDto, AutomationTeamDto } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  FlaskConical,
  FolderGit2,
  GitBranch,
  Play,
  Plus,
  Settings,
} from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  Badge,
  Button,
  buttonClassName,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  SkeletonRows,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { useAutomationCan, useAutomationTeam, useTeamRuns } from '../hooks';
import { AutomationStatusBadge, Mark, RunHistoryTable, useTriggerLabel } from '../ui';
import { AddProjectDialog } from './AddProjectDialog';
import { TeamSettingsDialog } from './TeamSettingsDialog';

const ProjectCard = ({ teamId, project }: { teamId: string; project: AutomationProjectDto }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const triggerLabel = useTriggerLabel();
  const later = project.lastPipeline;
  const laterTrigger = later ? (triggerLabel(later.trigger) ?? later.trigger) : '';

  return (
    <Link
      to="/automation/teams/$teamId/projects/$projectId"
      params={{ teamId, projectId: project.id }}
      search={{}}
      className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 transition-colors hover:border-border-strong hover:bg-canvas"
    >
      <div className="flex items-start justify-between gap-3">
        {/* min-w-0 lets a long name shrink instead of pushing the badges out of the card */}
        <div className="flex min-w-0 items-center gap-3">
          <Mark label={project.key} color={project.color} />
          <div className="min-w-0">
            <p className="truncate text-[14px] font-semibold text-ink">{project.name}</p>
            <p className="truncate text-[12px] text-ink-subtle">
              {t('automation.project.team.gitlabProject', { id: project.gitlabProjectId })}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {/* the icon alone: a word here pushed the run status out */}
          {project.note ? (
            <Badge className="bg-block-soft text-block" title={project.note}>
              <AlertTriangle className="size-3" aria-hidden="true" />
              <span className="sr-only">{t('automation.project.team.hasNote')}</span>
            </Badge>
          ) : null}
          {project.activeRuns > 0 ? (
            <AutomationStatusBadge status="running" />
          ) : project.lastRunStatus ? (
            <AutomationStatusBadge status={project.lastRunStatus} />
          ) : null}
        </div>
      </div>

      <div className="min-h-9 text-[13px]">
        {project.description ? <p className="text-ink-muted">{project.description}</p> : null}
        {later ? (
          <p
            className="text-[12px] text-ink-subtle"
            title={t('automation.project.team.laterPipelineHint', { trigger: laterTrigger })}
          >
            {t('automation.project.team.laterPipeline', {
              trigger: laterTrigger,
              status: t(`automation.status.${later.status}`),
              when: relative(later.at),
            })}
          </p>
        ) : null}
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 text-[13px]">
        <div className="flex items-center gap-3 text-ink-muted">
          <span className="inline-flex items-center gap-1">
            <FlaskConical className="size-3.5" aria-hidden="true" />
            {t('automation.projectTests', { count: project.testCount })}
          </span>
          <Badge>{project.testType}</Badge>
          {project.sourceProject ? (
            <span className="inline-flex items-center gap-1 text-[12px] text-ink-subtle">
              <GitBranch className="size-3" aria-hidden="true" />
              {t('automation.project.repo')}
            </span>
          ) : null}
        </div>
        <span className="inline-flex items-center gap-0.5 font-medium text-accent-fg">
          {t('automation.project.team.open')}
          <ChevronRight className="size-3.5" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
};

const TeamRuns = ({ teamId }: { teamId: string }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const can = useAutomationCan();
  const runs = useTeamRuns(teamId);

  return (
    <Card>
      <CardHeader
        title={t('automation.project.team.recentRuns')}
        action={
          can.run ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => void navigate({ to: '/automation/runs/new', search: {} })}
            >
              <Play className="size-3.5" />
              {t('automation.project.run')}
            </Button>
          ) : null
        }
      />
      {runs.error ? (
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      ) : !runs.data ? (
        <SkeletonRows rows={3} />
      ) : (
        <RunHistoryTable runs={runs.data} showProject />
      )}
    </Card>
  );
};

const TeamView = ({ team }: { team: AutomationTeamDto }) => {
  const { t } = useT();
  const can = useAutomationCan();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Mark label={team.key} color={team.color} size="lg" />
            {team.name}
          </span>
        }
        description={
          <span className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-ink-subtle">
              <span className="inline-flex items-center gap-1">
                <FolderGit2 className="size-3.5" aria-hidden="true" />
                {t('automation.project.teamProjects', { count: team.projects.length })}
              </span>
              <span>{t('automation.projectTests', { count: team.testCount })}</span>
              <span>{t('automation.project.teamMembers', { count: team.members.length })}</span>
            </span>
            {team.description ? <span>{team.description}</span> : null}
          </span>
        }
        actions={
          <>
            <Link to="/automation" className={buttonClassName('secondary')}>
              <ArrowLeft className="size-3.5" />
              {t('automation.project.team.back')}
            </Link>
            {can.manage ? (
              <>
                <Button
                  aria-label={t('automation.project.team.settings')}
                  title={t('automation.project.team.settings')}
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings className="size-4" />
                </Button>
                <Button variant="primary" onClick={() => setAddOpen(true)}>
                  <Plus className="size-4" />
                  {t('automation.project.addProject.title')}
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <section className="space-y-3">
        <h2 className="text-[14px] font-semibold text-ink">
          {t('automation.project.team.projects')}
        </h2>
        {team.projects.length === 0 ? (
          <Card>
            <EmptyState
              icon={<FolderGit2 className="size-6" />}
              title={t('automation.project.team.noProjects')}
              description={t('automation.project.team.noProjectsHint')}
              action={
                can.manage ? (
                  <Button variant="primary" onClick={() => setAddOpen(true)}>
                    <Plus className="size-4" />
                    {t('automation.project.addProject.title')}
                  </Button>
                ) : undefined
              }
            />
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {team.projects.map((project) => (
              <ProjectCard key={project.id} teamId={team.id} project={project} />
            ))}
          </div>
        )}
      </section>

      <TeamRuns teamId={team.id} />

      {settingsOpen ? <TeamSettingsDialog team={team} onOpenChange={setSettingsOpen} /> : null}
      {addOpen ? <AddProjectDialog teamId={team.id} onOpenChange={setAddOpen} /> : null}
    </>
  );
};

/** A team: its projects, its recent runs and, for admins, its settings. */
export function TeamPage({ teamId }: { teamId: string }) {
  const team = useAutomationTeam(teamId);

  // data first: after deleting, the refetch fails while the page navigates away
  if (team.data) return <TeamView team={team.data} />;
  if (team.error) {
    return (
      <Card>
        <ErrorState error={team.error} onRetry={() => void team.refetch()} />
      </Card>
    );
  }
  return <SkeletonRows rows={4} className="p-0" />;
}
